/**
 * PrepTaskListEditor — the ONE task-list editor for preparation requests,
 * used by both surfaces so they behave identically:
 *   - SubmitPrepRequest.jsx  (entering tasks at submission)
 *   - PrepRequestDetail.jsx  (the Adviser amending tasks afterwards, §26.7)
 *
 * Two levels: tasks (1., 2., 3.) and sub-tasks (1.a, 1.b …). Numbering is
 * derived from the array order on every render and never stored
 * (src/utils/prepTasks.js holds the model and every operation).
 *
 * Keyboard (outliner conventions):
 *   Enter          new empty line at the same level, directly after this one
 *   Tab            demote this line to a sub-task of the nearest preceding
 *                  top-level task (no-op on the first line / a sub-task)
 *   Shift+Tab      promote a sub-task to top level, placed after its former
 *                  parent's last sub-task
 *   Backspace      on an EMPTY line: remove it and focus the end of the
 *                  previous line (a parent's sub-tasks are promoted, never
 *                  deleted)
 * Buttons on each row (hover on desktop, always visible on touch) call the
 * same operations: indent, outdent, move up, move down, remove. Removing a
 * task that has sub-tasks asks for confirmation first; its sub-tasks are
 * kept as top-level tasks.
 *
 * `lockedIds` (amendment view): completed tasks are locked — rendered
 * struck-through with a padlock, no input, no controls (§26.7). The caller
 * carries their STORED record through on save, whatever this draft holds.
 *
 * Blank lines are dropped by the caller on save (dropBlankTasks), and the
 * caller always runs normalisePrepTasks before persisting — the operations
 * here keep the ordering invariant on their own; normalisation is the
 * safety net.
 */

import React, { useState, useCallback } from 'react';
import { IndentIncrease, IndentDecrease, ChevronUp, ChevronDown, X, Lock } from 'lucide-react';
import { nb } from './NotebookPaper';
import TaskLineInput from './TaskLineInput';
import {
  numberPrepTasks,
  insertTaskAfter,
  appendTask,
  demoteTask,
  promoteTask,
  canDemoteTask,
  canPromoteTask,
  moveTask,
  removeTask,
  newPrepTask,
} from '../../utils/prepTasks';

const SUB_INDENT = '36px';

export default function PrepTaskListEditor({
  tasks,
  onChange,
  lockedIds,
  disabled = false,
  placeholder = 'Add a task…',
  addLabel = '+ add another task',
}) {
  const locked = lockedIds instanceof Set ? lockedIds : new Set(lockedIds || []);
  const list = Array.isArray(tasks) ? tasks : [];
  const [focusId, setFocusId] = useState(null);
  const clearFocus = useCallback(() => setFocusId(null), []);

  // Every change goes through here so a caller-supplied array is never
  // mutated and an operation that changed nothing is a true no-op.
  const commit = (next) => {
    if (next !== list) onChange(next);
  };

  const numbered = numberPrepTasks(list);
  const isLocked = (t) => locked.has(t.id);

  const handleEnter = (id) => {
    const { tasks: next, id: newId } = insertTaskAfter(list, id);
    commit(next);
    setFocusId(newId);
  };

  const handleAdd = () => {
    const { tasks: next, id: newId } = appendTask(list);
    commit(next);
    setFocusId(newId);
  };

  const handleRemove = (t, viaBackspace = false) => {
    const kids = list.filter((k) => k.parent === t.id);
    if (!viaBackspace && kids.length > 0) {
      const ok = window.confirm(
        `Remove this task? Its ${kids.length} sub-task${kids.length === 1 ? '' : 's'} will be kept as top-level tasks.`
      );
      if (!ok) return;
    }
    let next = removeTask(list, t.id);
    // Never leave the editor empty — a fresh blank line stays for typing.
    if (next.length === 0) next = [newPrepTask()];
    commit(next);
    if (viaBackspace) {
      // Focus the end of the previous editable line, if there is one.
      const pos = numbered.findIndex((row) => row.task.id === t.id);
      const prev = numbered.slice(0, pos).reverse().find((row) => !isLocked(row.task));
      if (prev) setFocusId(prev.task.id);
      else if (next.length === 1 && next[0].id !== t.id) setFocusId(next[0].id);
    }
  };

  const handleBackspaceEmpty = (t) => {
    // The only line on the page stays (there is nothing to fall back to).
    if (list.length <= 1) return;
    handleRemove(t, true);
  };

  return (
    <>
      <div style={nb.tasks}>
        {numbered.map(({ task: t, label, depth }, rowIndex) => {
          const lockedRow = isLocked(t);
          const canIn = !lockedRow && canDemoteTask(list, t.id);
          const canOut = !lockedRow && canPromoteTask(list, t.id);
          return (
            <div
              className="np-task-row"
              key={t.id || `row-${rowIndex}`}
              style={{
                ...nb.taskRow,
                alignItems: 'flex-start',
                paddingLeft: depth === 1 ? SUB_INDENT : 0,
              }}
            >
              <span style={{ ...nb.taskNum, lineHeight: '36px', minWidth: depth === 1 ? '40px' : '24px' }}>
                {label}
              </span>
              {lockedRow ? (
                /* Locked: a ticked task records work already done, so it
                   cannot be edited, reordered or removed by anyone — the
                   Adviser included. Struck-through muted treatment plus a
                   padlock. */
                <>
                  <Lock size={13} color="#a9b6c6" style={{ flex: '0 0 auto', marginTop: '12px' }} aria-hidden="true" />
                  <span
                    title="Completed tasks can't be changed"
                    style={{
                      ...nb.summaryText,
                      flex: 1,
                      whiteSpace: 'pre-wrap',
                      textDecoration: 'line-through',
                      color: '#6B7A8D',
                      lineHeight: '36px',
                    }}
                  >
                    {t.text}
                  </span>
                </>
              ) : (
                <>
                  <TaskLineInput
                    value={t.text || ''}
                    onChange={(text) => commit(list.map((k) => (k.id === t.id ? { ...k, text } : k)))}
                    onEnter={() => handleEnter(t.id)}
                    onTab={() => commit(demoteTask(list, t.id))}
                    onShiftTab={() => commit(promoteTask(list, t.id))}
                    onBackspaceEmpty={() => handleBackspaceEmpty(t)}
                    focusRequested={focusId === t.id}
                    onFocusHandled={clearFocus}
                    placeholder={depth === 1 ? 'Add a sub-task…' : placeholder}
                    disabled={disabled}
                    ariaLabel={`Task ${label}`}
                  />
                  <div className="np-task-tools" style={{ display: 'flex', gap: '2px', flex: '0 0 auto', height: '36px', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="np-task-remove"
                      onClick={() => commit(demoteTask(list, t.id))}
                      disabled={disabled || !canIn}
                      aria-label={`Make task ${label} a sub-task`}
                      title="Indent (Tab)"
                    >
                      <IndentIncrease size={15} />
                    </button>
                    <button
                      type="button"
                      className="np-task-remove"
                      onClick={() => commit(promoteTask(list, t.id))}
                      disabled={disabled || !canOut}
                      aria-label={`Make task ${label} a top-level task`}
                      title="Outdent (Shift+Tab)"
                    >
                      <IndentDecrease size={15} />
                    </button>
                    <button
                      type="button"
                      className="np-task-remove"
                      onClick={() => commit(moveTask(list, t.id, -1))}
                      disabled={disabled}
                      aria-label={`Move task ${label} up`}
                      title="Move up"
                    >
                      <ChevronUp size={15} />
                    </button>
                    <button
                      type="button"
                      className="np-task-remove"
                      onClick={() => commit(moveTask(list, t.id, 1))}
                      disabled={disabled}
                      aria-label={`Move task ${label} down`}
                      title="Move down"
                    >
                      <ChevronDown size={15} />
                    </button>
                    <button
                      type="button"
                      className="np-task-remove"
                      onClick={() => handleRemove(t)}
                      disabled={disabled}
                      aria-label={`Remove task ${label}`}
                      title="Remove task"
                    >
                      <X size={15} />
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <button type="button" className="np-add-task" onClick={handleAdd} disabled={disabled}>
        {addLabel}
      </button>
    </>
  );
}
